import { useCallback, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CreateRowConfigDto } from '@asset-metrix/portal-gateway-client-js';
import {
  Button,
  fileReducer,
  GoogleFontIcon,
  Modal,
  Stepper,
  SVGIcon,
} from '@asset-metrix/portal-ui';
import { JsonError } from '@sagold/react-json-editor';

import {
  parseDataIngestionTemplate,
  type ParseTemplateValidationError,
} from '../../api/portalGateway/parseDataIngestionTemplate';
import { extractErrorMessage } from '../../utils/errorMessage';
import { pixelSizeHelperPortalUi } from '../../utils/pixelSizeHelperPortalUi';

import { ConfigureAndDownloadStep } from './ConfigureAndDownloadStep';
import { UploadAndParseStep } from './UploadAndParseStep';

import styles from './dataIngestionModal.module.scss';
export type DataIngestionModalProps = {
  opened: boolean;
  onClose: () => void;
  onUploadSuccess: () => Promise<void> | void;
  config: CreateRowConfigDto;
  contentId: string;
  instanceId: string;
};

const CONFIGURE_AND_DOWNLOAD = 0;
const UPLOAD_AND_PARSE = 1;

export function DataIngestionModal(props: DataIngestionModalProps) {
  const { opened, onClose, onUploadSuccess, config, contentId, instanceId } =
    props;
  const [activeStep, setActiveStep] = useState(CONFIGURE_AND_DOWNLOAD);
  const [validationErrors, setValidationErrors] = useState<JsonError[]>([]);
  const [fileState, dispatch] = useReducer(fileReducer, []);
  const [importData, setImportData] = useState<Record<string, unknown>>({});
  const [uploadStatus, setUploadStatus] = useState<
    | {
        state: 'pending' | 'success' | 'error';
        message?: string;
        errors?: ParseTemplateValidationError[];
      }
    | undefined
  >();

  const { t } = useTranslation();

  const resetModalState = useCallback(() => {
    setActiveStep(CONFIGURE_AND_DOWNLOAD);
    setValidationErrors([]);
    setImportData({});
    setUploadStatus(undefined);
    dispatch({ type: 'resetFileSelections' });
  }, [dispatch]);

  const handleClose = useCallback(() => {
    resetModalState();
    onClose();
  }, [onClose, resetModalState]);

  const handleUpload = useCallback(async () => {
    setUploadStatus({ state: 'pending' });
    try {
      const result = await parseDataIngestionTemplate(contentId, {
        file: fileState[0].file,
        importData: importData as Record<
          string,
          string | number | boolean | null
        >,
      });

      if (result.status === 'fail') {
        setUploadStatus({
          state: 'error',
          message: result.message,
          errors: result.errors,
        });
        return;
      }

      setUploadStatus({ state: result.status });

      try {
        await onUploadSuccess();
      } catch (error) {
        console.error('onUploadSuccess handler failed', error);
      }
    } catch (error) {
      console.error(error);
      setUploadStatus({ state: 'error', message: extractErrorMessage(error) });
    }
  }, [fileState, importData, contentId, onUploadSuccess]);

  const isUploadPending = uploadStatus?.state === 'pending';
  const isUploadSuccess = uploadStatus?.state === 'success';
  const uploadButtonIcon = isUploadSuccess ? (
    <GoogleFontIcon name="check" />
  ) : (
    <SVGIcon name="upload" />
  );
  const uploadButtonText = isUploadSuccess
    ? t('app.dataIngestion.done')
    : isUploadPending
      ? t('app.dataIngestion.uploading')
      : t('app.dataIngestion.upload');
  const handleUploadButtonClick = isUploadSuccess ? handleClose : handleUpload;

  const steps = [
    { label: t('app.dataIngestion.configureAndDownload.title') },
    { label: t('app.dataIngestion.uploadAndParse.title') },
  ];

  return (
    <Modal
      contentClassName={styles['modalContent']}
      actions={
        <>
          <Button variant="secondary" onClick={handleClose}>
            {t('app.dataIngestion.cancel')}
          </Button>
          {activeStep === CONFIGURE_AND_DOWNLOAD && (
            <Button
              onClick={() => setActiveStep(UPLOAD_AND_PARSE)}
              disabled={
                Boolean(config.jsonSchema) && validationErrors.length > 0
              }
            >
              {t('app.dataIngestion.next')}
            </Button>
          )}
          {activeStep === UPLOAD_AND_PARSE && (
            <Button
              icon={uploadButtonIcon}
              onClick={handleUploadButtonClick}
              disabled={fileState.length === 0 || isUploadPending}
              loading={isUploadPending}
            >
              {uploadButtonText}
            </Button>
          )}
        </>
      }
      modalType="action"
      opened={opened}
      onClose={handleClose}
      size={pixelSizeHelperPortalUi(1200)}
      contentHeight="90vh"
      title={t('app.dataIngestion.title')}
    >
      <Stepper steps={steps} active={activeStep} allowStepClick={false} />
      {activeStep === CONFIGURE_AND_DOWNLOAD ? (
        <ConfigureAndDownloadStep
          config={config}
          contentId={contentId}
          instanceId={instanceId}
          onValidationErrorsChange={setValidationErrors}
          onImportDataChange={setImportData}
        />
      ) : (
        <UploadAndParseStep
          fileState={fileState}
          dispatch={dispatch}
          uploadStatus={uploadStatus}
        />
      )}
    </Modal>
  );
}
